"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EffectTracker = exports.MAX_TOAST_MESSAGE_LENGTH = exports.DEFAULT_TOAST_DURATION_MS = exports.MAX_TOASTS_PER_EFFECT = void 0;
exports.normalizeToastType = normalizeToastType;
exports.normalizeToastMessage = normalizeToastMessage;
exports.emptyEffectResult = emptyEffectResult;
exports.mergeEffectInto = mergeEffectInto;
exports.mergeEffectResults = mergeEffectResults;
/** Maximum toast notifications recorded per effect script execution. */
exports.MAX_TOASTS_PER_EFFECT = 10;
/** Default toast visibility duration when not specified (ms). */
exports.DEFAULT_TOAST_DURATION_MS = 5000;
/** Maximum toast message length (truncated with no throw). */
exports.MAX_TOAST_MESSAGE_LENGTH = 500;
const TOAST_TYPES = new Set([
    'success',
    'error',
    'warning',
    'info'
]);
function normalizeToastType(type) {
    const t = (type ?? 'info').toString().trim().toLowerCase();
    return TOAST_TYPES.has(t) ? t : 'info';
}
function normalizeToastMessage(message) {
    const raw = (message ?? '').toString();
    const stripped = raw.replace(/<[^>]*>/g, '');
    const trimmed = stripped.trim();
    if (trimmed.length <= exports.MAX_TOAST_MESSAGE_LENGTH) {
        return trimmed;
    }
    return trimmed.slice(0, exports.MAX_TOAST_MESSAGE_LENGTH);
}
function emptyEffectResult() {
    return { itemChanges: [], statChanges: [], toasts: [] };
}
/** Append all fields from {@code source} into {@code target} (mutates target). */
function mergeEffectInto(target, source) {
    target.itemChanges.push(...source.itemChanges);
    target.statChanges.push(...source.statChanges);
    target.toasts.push(...source.toasts);
}
/** Combine multiple effect results in order (option effect + auto effect + NPC tick, etc.). */
function mergeEffectResults(...sources) {
    const merged = emptyEffectResult();
    for (const source of sources) {
        mergeEffectInto(merged, source);
    }
    return merged;
}
/**
 * Tracks item, stat, and toast side effects from a single effect script execution.
 */
class EffectTracker {
    constructor() {
        this.itemChanges = [];
        this.statChanges = [];
        this.toasts = [];
    }
    trackItemAdded(tag) {
        this.itemChanges.push({
            itemTag: tag,
            changeType: 'added',
            timestamp: Date.now()
        });
    }
    trackItemRemoved(tag) {
        this.itemChanges.push({
            itemTag: tag,
            changeType: 'removed',
            timestamp: Date.now()
        });
    }
    trackStatAdded(name) {
        this.statChanges.push({
            statName: name,
            changeType: 'added'
        });
    }
    trackStatRemoved(name) {
        this.statChanges.push({
            statName: name,
            changeType: 'removed'
        });
    }
    trackCharacterChanged(_characterName) {
        // Character changes are synced via player state updates, not EffectResult.
    }
    /**
     * Queue a character-targeted toast for delivery after the effect completes.
     * No-op when character name or message is empty, or when the per-run cap is reached.
     */
    trackToast(characterName, message, type, durationMs) {
        const character = (characterName ?? '').toString().trim();
        const normalizedMessage = normalizeToastMessage(message);
        if (!character || !normalizedMessage) {
            return;
        }
        if (this.toasts.length >= exports.MAX_TOASTS_PER_EFFECT) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`[EffectTracker] Toast cap (${exports.MAX_TOASTS_PER_EFFECT}) reached; dropping toast for '${character}'`);
            }
            return;
        }
        const duration = Number(durationMs);
        const resolvedDuration = Number.isFinite(duration) && duration > 0 ? Math.floor(duration) : exports.DEFAULT_TOAST_DURATION_MS;
        this.toasts.push({
            characterName: character,
            message: normalizedMessage,
            type: normalizeToastType(type),
            durationMs: resolvedDuration
        });
    }
    getResult() {
        return {
            itemChanges: this.itemChanges,
            statChanges: this.statChanges,
            toasts: [...this.toasts]
        };
    }
}
exports.EffectTracker = EffectTracker;
//# sourceMappingURL=EffectTracker.js.map